// Best-effort screenshot capture for the feedback widget. Captures the app
// view as a downscaled JPEG data URL, or returns null if anything goes wrong —
// it NEVER throws, so the caller always submits (with or without the image).
//
// Uses html2canvas-pro (the library the app already uses for chart/deck export
// in chartExport.ts). html-to-image was tried first but took >30s to serialize
// this app's DOM (~3k nodes); html2canvas-pro captures the same view in ~2s.
//
// Only the app's own DOM is captured — it can't reach the OS screen or other
// tabs. The feedback widget itself is excluded (tagged `data-feedback-widget`)
// so the image shows the page the feedback is about, not the panel over it.

// Cap the long edge so a large monitor's DOM doesn't produce a huge image.
const MAX_WIDTH = 1600;
const QUALITY = 0.7;
// Overall guard: real captures are ~2s (html2canvas-pro over ~3k nodes). Capture
// runs when the panel opens (overlapping the user's typing), so a generous
// ceiling is fine — the submit consumes whatever is ready and never waits long.
const CAPTURE_TIMEOUT_MS = 8000;
// Per-image ceiling so one slow/cross-origin asset can't stall the whole capture.
const IMAGE_TIMEOUT_MS = 2000;

/** True for the feedback widget subtree — html2canvas skips ignored elements. */
function isWidget(el: Element): boolean {
  return el instanceof HTMLElement && el.dataset?.feedbackWidget !== undefined;
}

/**
 * Capture the current view as a base64 JPEG data URL, or null on any failure /
 * timeout. Safe to call unconditionally.
 */
export async function captureScreenshot(): Promise<string | null> {
  const root = document.getElementById("root");
  if (!root || typeof root.scrollWidth !== "number" || root.scrollWidth === 0) return null;

  // scale < 1 downscales; keep it at 1 unless the view is wider than the cap.
  const scale = root.scrollWidth > MAX_WIDTH ? MAX_WIDTH / root.scrollWidth : 1;

  try {
    const html2canvas = (await import("html2canvas-pro")).default;
    const capture = html2canvas(root, {
      scale,
      useCORS: true,
      // Never taint the canvas — a tainted canvas throws on toDataURL. A
      // cross-origin image without CORS is dropped from the shot instead.
      allowTaint: false,
      imageTimeout: IMAGE_TIMEOUT_MS,
      backgroundColor: "#ffffff",
      logging: false,
      foreignObjectRendering: false,
      ignoreElements: isWidget,
    }).then((canvas) => canvas.toDataURL("image/jpeg", QUALITY));

    const timeout = new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS);
    });
    const started = performance.now();
    const result = await Promise.race([capture, timeout]);
    if (result == null) {
      console.warn(
        `[feedback] screenshot capture timed out after ${Math.round(performance.now() - started)}ms`
      );
      return null;
    }
    return typeof result === "string" && result.startsWith("data:image/") ? result : null;
  } catch (err) {
    console.warn("[feedback] screenshot capture failed:", err instanceof Error ? err.message : err);
    return null;
  }
}
